/* eslint-disable no-undef */
import axios from 'axios';
import Cookies from 'js-cookie';
import snackbar from '../snackbar';
import siteApi from './siteApi';

/**
 * Api - Class representing axios instance for Club API.
 * Handles initial configuration and generic error handling
 */
class ClubApi {
  constructor() {
    this.accessToken = null;
    this.appElement = document.getElementById('app');
    this.instance = axios.create({
      // Articles API will use current url as base url
      // For club, photobook base url should be defined like example below
      baseURL: `${API_CLUB_URL}`,
    });

    // Default Error handling
    this.instance.interceptors.response.use(
      (res) => res,
      (err) => {
        const originalRequest = err.config;
        // Define generic error handling
        if (err.response.status === 400) {
          snackbar.show(err.response.data.message);
        } else if (err.response.status === 401 && !originalRequest.retry) {
          originalRequest.retry = true;

          // If bdp.fe expired this will try to refresh access token and retry user request
          return this.getToken().then(() => {
            originalRequest.headers.Authorization = `Bearer ${this.accessToken}`;
            return this.instance.request(originalRequest);
          });
        } else if (err.response.status === 404) {
          snackbar.show(err.response.data.message);
          return Promise.reject(err);
        } else if (err.response.status === 500) {
          // Backend will render translated generic error message on initial page load
          snackbar.show(window.errorMessage);
          return Promise.reject(err);
        }
        return Promise.reject(err);
      },
    );

    // Set header access token or update if don't exists
    if (this.appElement) {
      this.accessToken = Cookies.get('bdp.fe');
      if (this.accessToken) {
        this.setToken(this.accessToken);
      }
    }
  }

  /**
   * Set Access token to header
   * @param {String} token Set Access token
   */
  setToken(token) {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  /**
   * Show Notification
   */
  showNotification(message) {
    snackbar.show(message);
  }

  /**
   * Get access token and set for API usage
   * accessToken cookie will be set from server-side once getToken function is called
   */
  async getToken() {
    try {
      if (!siteApi.antiForgeryToken) {
        await siteApi.loadAntiForgeryToken();
      }
      siteApi.instance.defaults.headers['__RequestVerificationToken'] = siteApi.antiForgeryToken;
      const response = await siteApi.post('/refreshaccesstoken/');
      Cookies.set('bdp.fe', response.data.Value, { expires: new Date(response.data.Expires) });
      this.accessToken = Cookies.get('bdp.fe');
      if (this.accessToken) {
        this.setToken(this.accessToken);
      }
      return this.accessToken;
    } catch {
      return null;
    }
  }

  /**
   * Get method
   * @param  {...any} params
   */
  get(...params) {
    return this.instance.get(...params);
  }

  /**
   * Post method
   * @param  {...any} params
   */
  post(...params) {
    return this.instance.post(...params);
  }

  /**
   * Put method
   * @param  {...any} params
   */
  put(...params) {
    return this.instance.put(...params);
  }

  /**
   * Patch method
   * @param  {...any} params
   */
  patch(...params) {
    return this.instance.patch(...params);
  }

  /**
   * Delete method
   * @param  {...any} params
   */
  delete(...params) {
    return this.instance.delete(...params);
  }
}
const clubApi = new ClubApi();
export default clubApi;
